import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Modal,
  List,
  ListItem,
  ListItemText,
  Divider,
  Card,
  CardContent,
  Stack,
  TextField,
  Chip,
  Tooltip,
  IconButton,
  Tab,
  Tabs,
  Avatar
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import api from '../../config/api';
import dayjs, { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { 
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  NewReleases as NewIcon,
  Pending as InProgressIcon,
  AssignmentTurnedIn as ResolvedIcon,
  HistoryToggleOff as NotResolvedIcon,
  Visibility as ViewIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Store as StoreIcon,
  Comment as CommentIcon,
  FileDownload as FileDownloadIcon
} from '@mui/icons-material';

interface CommentCard {
  customername: string;
  salesnumber: string;
  phone: string;
  brandid: string;
  brandname: string;
  outletid: string;
  outletname: string;
  question: string;
  answer: string;
  pooranswer: string;
  typechoice: string;
  submitdate: string;
  status?: string;
  response_category?: string;
  source?: string;
}

interface Brand {
  brand_id: string;
  brand_name: string;
}

interface QuestionnaireItem {
  question: string;
  answer: string;
  pooranswer: string;
}

interface Question {
  id: string;
  text: string;
}

interface GCCDetail {
  customer_name: string;
  customer_phone: string;
  sales_number: string;
  brand_name: string;
  outlet_name: string;
  submit_date: string;
  outlet_address?: string;
  outlet_phone?: string;
  status?: string;
  questionnaire: QuestionnaireItem[];
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

const GuestCommentCardPage: React.FC = () => {
  const navigate = useNavigate();
  const [commentCards, setCommentCards] = useState<CommentCard[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().subtract(7, 'day'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [filterPoorAnswer, setFilterPoorAnswer] = useState<boolean>(false);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [selectedBrand, setSelectedBrand] = useState<string>('');
  const [loadingBrands, setLoadingBrands] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedSalesNumber, setSelectedSalesNumber] = useState<string>('');
  const [questionnaires, setQuestionnaires] = useState<QuestionnaireItem[]>([]);
  const [loadingQuestionnaires, setLoadingQuestionnaires] = useState<boolean>(false);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [selectedQuestion, setSelectedQuestion] = useState<string>('');
  const [loadingQuestions, setLoadingQuestions] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [gccDetail, setGCCDetail] = useState<GCCDetail | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [exportingGccCsv, setExportingGccCsv] = useState(false);

  // Check token validity
  const checkTokenValidity = (): boolean => {
    const token = localStorage.getItem('token');
    
    if (!token) {
      console.log('No token found');
      setError('You are not logged in. Please log in to continue.');
      return false;
    }
    
    return true;
  };

  // Add error handling helper function
  const handleApiError = (err: any, errorMessage: string) => {
    console.error(errorMessage, err);
    
    if (err.response?.status === 401) {
      setError('Authentication error. Please log in again.');
      // Optionally redirect to login page
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } else {
      setError(`${errorMessage} ${err.response?.data?.message || ''}`);
    }
  };

  // Fetch brands the user has access to
  useEffect(() => {
    const fetchBrands = async () => {
      setLoadingBrands(true);
      try {
        const response = await api.get('/feedback/brands');
        
        if (response.data.data) {
          // Check if user has all brands access
          const hasAllBrandsAccess = response.data.hasAllBrandsAccess;
          
          // Filter to get unique brand IDs and names
          const uniqueBrands = Array.from(
            new Map(
              response.data.data
                .filter((item: any) => item.brand_id && item.brand_name)
                .map((item: any) => [item.brand_id, { brand_id: item.brand_id, brand_name: item.brand_name }])
            ).values()
          );
          
          // If user has all brands access, add the AB option
          if (hasAllBrandsAccess) {
            uniqueBrands.unshift({ brand_id: 'AB', brand_name: 'All Brands' });
          }
          
          setBrands(uniqueBrands as Brand[]);
        }
      } catch (err) {
        handleApiError(err, 'Error fetching brands:');
      } finally {
        setLoadingBrands(false);
      }
    };
    
    fetchBrands();
  }, []);

  // Fetch questions for filtering
  useEffect(() => {
    const fetchQuestions = async () => {
      setLoadingQuestions(true);
      try {
        const response = await api.get('/comment-card/questions');
        
        if (response.data.success) {
          // Filter to get unique questions by TEXT, not ID
          const uniqueQuestions = Array.from(
            new Map(
              (response.data.data || [])
                .filter((item: Question) => item.text && item.text.trim() !== '') // Filter out empty text
                .map((item: Question) => [item.text, item])
            ).values()
          ) as Question[];
          
          setQuestions(uniqueQuestions);
        }
      } catch (err) {
        handleApiError(err, 'Error fetching questions:');
      } finally {
        setLoadingQuestions(false);
      }
    };
    
    fetchQuestions();
  }, []);

  // Add initial data load effect
  useEffect(() => {
    // Only fetch data if we have date values
    if (startDate && endDate) {
      // Use a try-catch in case the initial data load fails
      try {
        fetchCommentCards();
      } catch (err) {
        // Already handled in fetchCommentCards
        console.log('Initial data load failed, user will need to click Search');
      }
    }
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // Add effect for pagination changes
  useEffect(() => {
    if (startDate && endDate) {
      fetchCommentCards();
    }
  }, [page, rowsPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCommentCards = async () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    // Check if token exists before making API calls
    if (!checkTokenValidity()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formattedStartDate = startDate.format('YYYY-MM-DD');
      const formattedEndDate = endDate.format('YYYY-MM-DD');
      
      const params: any = {
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        filterPoorAnswer: filterPoorAnswer,
        page: page + 1, // MUI pagination is 0-based, API is 1-based
        limit: rowsPerPage
      };
      
      // Add brandId parameter if a brand is selected
      if (selectedBrand && selectedBrand !== 'AB') {
        params.brandId = selectedBrand;
      }

      // Add questionText parameter if a question is selected
      if (selectedQuestion) {
        params.questionText = selectedQuestion;
      }

      // Add search query if provided
      if (searchQuery) {
        params.search = searchQuery;
      }
      
      // Add status filter if not "all"
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      
      const response = await api.get('/comment-card/guest-comment-cards', { params });

      if (response.data.success) {
        setCommentCards(response.data.data);
        
        // Update pagination from the response
        if (response.data.pagination) {
          setTotalRecords(response.data.pagination.total);
        }
      } else {
        setError(response.data.message || 'Failed to fetch data');
      }
    } catch (err) {
      handleApiError(err, 'Error fetching comment cards:');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    fetchCommentCards(); // Fetch new data when page changes
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page
    fetchCommentCards(); // Fetch new data with updated rows per page
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    try {
      return dayjs(dateString).format('DD MMM YYYY HH:mm');
    } catch (e) {
      return dateString;
    }
  };

  const handleSalesNumberClick = async (salesNumber: string) => {
    // Check if token exists before making API calls
    if (!checkTokenValidity()) {
      return;
    }
    
    setSelectedSalesNumber(salesNumber);
    setLoadingQuestionnaires(true);
    setGCCDetail(null);
    setModalOpen(true);
    
    try {
      // Updated endpoint to fetch all GCC data for the sales number
      const response = await api.get('/comment-card/all-gcc-data', {
        params: { salesNumber }
      });
      
      if (response.data.success) {
        // Store the questionnaires separately for backward compatibility
        setQuestionnaires(response.data.data.questionnaire || []);
        
        // Store the complete GCC detail
        setGCCDetail(response.data.data);
      } else {
        console.error('Failed to fetch GCC data:', response.data.message);
      }
    } catch (err) {
      handleApiError(err, 'Error fetching GCC data:');
    } finally {
      setLoadingQuestionnaires(false);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedSalesNumber('');
    setQuestionnaires([]);
    setGCCDetail(null);
  };

  const handleExportGuestCommentCards = async () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }
    if (!checkTokenValidity()) {
      return;
    }

    const inclusiveDays =
      endDate.startOf('day').diff(startDate.startOf('day'), 'day') + 1;
    if (inclusiveDays < 1) {
      setError('End date must be on or after the start date.');
      return;
    }
    if (inclusiveDays > 31) {
      setError(
        `Export allows at most 31 calendar days (${inclusiveDays} days selected).`
      );
      return;
    }

    setExportingGccCsv(true);
    setError(null);

    try {
      const formattedStartDate = startDate.format('YYYY-MM-DD');
      const formattedEndDate = endDate.format('YYYY-MM-DD');
      const params: Record<string, string | boolean> = {
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        filterPoorAnswer,
      };
      if (selectedBrand && selectedBrand !== 'AB') {
        params.brandId = selectedBrand;
      }
      if (selectedQuestion) params.questionText = selectedQuestion;
      if (searchQuery) params.search = searchQuery;
      if (statusFilter !== 'all') params.status = statusFilter;

      const response = await api.get('/comment-card/guest-comment-cards/export', {
        params,
        responseType: 'blob',
      });

      const ctype =
        typeof response.headers['content-type'] === 'string'
          ? response.headers['content-type']
          : '';
      if (ctype.includes('application/json')) {
        const txt = await (response.data as Blob).text();
        try {
          const parsed = JSON.parse(txt) as { message?: string };
          setError(parsed.message || 'Export failed');
        } catch {
          setError('Export failed');
        }
        return;
      }

      const blob = response.data as Blob;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const cd = response.headers['content-disposition'] as string | undefined;
      const fnMatch =
        cd && /filename\*=UTF-8''([^;\n]+)/i.exec(cd);
      let fname = '';
      if (fnMatch?.[1]) {
        try {
          fname = decodeURIComponent(fnMatch[1].trim());
        } catch {
          fname = '';
        }
      }
      if (!fname) {
        const qMatch = cd && /filename="([^"]+)"/.exec(cd);
        fname =
          qMatch?.[1] ||
          `guest-comment-cards-${formattedStartDate}_${formattedEndDate}.csv`;
      }
      link.setAttribute('download', fname);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: unknown) {
      console.error('Error exporting guest comment cards:', err);
      let message = 'Error exporting guest comment cards';
      if (
        typeof err === 'object' &&
        err !== null &&
        'response' in err &&
        typeof (err as { response?: unknown }).response === 'object' &&
        (err as { response?: { status?: number; data?: Blob } }).response !==
          null
      ) {
        const res = (
          err as { response?: { status?: number; data?: Blob; statusText?: string } }
        ).response!;
        const data = res.data;
        if (data instanceof Blob) {
          try {
            const txt = await data.text();
            try {
              const parsed = JSON.parse(txt) as { message?: string };
              message =
                parsed.message ||
                `${message} (${res.status ?? ''} ${res.statusText ?? ''})`.trim();
            } catch {
              message = txt.trim() ? txt.slice(0, 200) : message;
            }
          } catch {
            message =
              `${message} (${res.status ?? ''} ${res.statusText ?? ''})`.trim();
          }
        }
      }
      setError(message);
    } finally {
      setExportingGccCsv(false);
    }
  };

  // Add this helper function to get status chip styles
  const getStatusChip = (status?: string) => {
    if (!status) return <Chip size="small" label="New" color="info" icon={<NewIcon />} />;
    
    switch (status.toLowerCase()) {
      case 'new':
        return <Chip size="small" label="New" color="info" icon={<NewIcon />} />;
      case 'in_progress':
        return <Chip size="small" label="In Progress" color="warning" icon={<InProgressIcon />} />;
      case 'resolved':
        return <Chip size="small" label="Resolved" color="success" icon={<ResolvedIcon />} />;
      case 'not_resolved':
        return <Chip size="small" label="Not Resolved" color="error" icon={<NotResolvedIcon />} />;
      default:
        return <Chip size="small" label={status} color="default" />;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Guest Comment Cards
      </Typography>
      
      {/* Error Display */}
      {error && (
        <Alert 
          severity={error.includes('Authentication') || error.includes('logged in') ? 'warning' : 'error'} 
          sx={{ mb: 3, display: 'flex', alignItems: 'center' }}
          action={
            error.includes('Authentication') || error.includes('logged in') ? (
              <Button 
                color="inherit" 
                size="small" 
                onClick={() => navigate('/login')}
              >
                Login
              </Button>
            ) : null
          }
        >
          {error}
        </Alert>
      )}
      
      {/* Filter Panel */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => {
                  setStartDate(newValue);
                  setPage(0);
                  fetchCommentCards();
                }}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => {
                  setEndDate(newValue);
                  setPage(0);
                  fetchCommentCards();
                }}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel id="brand-select-label">Brand</InputLabel>
              <Select
                labelId="brand-select-label"
                id="brand-select"
                value={selectedBrand}
                onChange={(e) => {
                  setSelectedBrand(e.target.value);
                  setPage(0);
                  fetchCommentCards();
                }}
                label="Brand"
                disabled={loadingBrands}
              >
                <MenuItem value="">
                  <em>Select a Brand</em>
                </MenuItem>
                {brands.map((brand) => (
                  <MenuItem 
                    key={brand.brand_id} 
                    value={brand.brand_id}
                    sx={{
                      fontWeight: brand.brand_id === 'AB' ? 'bold' : 'normal',
                      color: brand.brand_id === 'AB' ? 'primary.main' : 'inherit'
                    }}
                  >
                    {brand.brand_name}
                    {brand.brand_id === 'AB' && (
                      <Box component="span" sx={{ ml: 1, color: 'success.main', fontSize: '0.8em' }}>
                        (Full Access)
                      </Box>
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel id="question-select-label">Question</InputLabel>
              <Select
                labelId="question-select-label"
                id="question-select"
                value={selectedQuestion}
                onChange={(e) => {
                  setSelectedQuestion(e.target.value);
                  setPage(0);
                  fetchCommentCards();
                }}
                label="Question"
                disabled={loadingQuestions}
              >
                <MenuItem value="">All Questions</MenuItem>
                {questions.map((question) => (
                  <MenuItem key={question.text} value={question.text}>
                    {question.text}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel id="status-select-label">Status</InputLabel>
              <Select
                labelId="status-select-label"
                id="status-select"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                  fetchCommentCards();
                }}
                label="Status"
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="new">New</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
                <MenuItem value="not_resolved">Not Resolved</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="Search"
              variant="outlined"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(0);
                fetchCommentCards();
              }}
              placeholder="Customer name, phone..."
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filterPoorAnswer}
                  onChange={(e) => {
                    setFilterPoorAnswer(e.target.checked);
                    setPage(0);
                    fetchCommentCards();
                  }}
                  color="primary"
                />
              }
              label="Show only poor answers"
            />
          </Grid>
          
          <Grid item xs={12}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="contained"
                onClick={fetchCommentCards}
                fullWidth
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Search'}
              </Button>
              <Tooltip
                title="Uses your current filters. Only brands you can access. Max 31 calendar days per file. Allowed 20:00–10:00 Asia/Jakarta."
                placement="top"
              >
                <span style={{ flex: 1 }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<FileDownloadIcon />}
                    onClick={handleExportGuestCommentCards}
                    disabled={loading || exportingGccCsv}
                  >
                    {exportingGccCsv ? (
                      <CircularProgress size={22} />
                    ) : (
                      'Export CSV'
                    )}
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Analytics Section - Disabled for performance reasons */}
      
      {/* Data Table */}
      <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'primary.light' }}>
                <TableCell>Customer</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell>Brand/Outlet</TableCell>
                <TableCell>Feedback</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : commentCards.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body1" sx={{ my: 2 }}>
                      No records found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                commentCards
                  .map((card, index) => (
                    <TableRow 
                      key={index}
                      sx={{ 
                        '&:nth-of-type(odd)': { backgroundColor: 'action.hover' },
                        '&:hover': { backgroundColor: 'action.selected' }
                      }}
                    >
                      {/* Customer column */}
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <PersonIcon fontSize="small" color="primary" />
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {card.customername || 'Anonymous'}
                            </Typography>
                            {card.phone && (
                              <Typography variant="caption" display="flex" alignItems="center" gap={0.5}>
                                <PhoneIcon fontSize="inherit" />
                                {card.phone}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      
                      {/* Reference column */}
                      <TableCell>
                        <Tooltip title="Click to view all details">
                          <Box
                            component="span"
                            onClick={() => card.salesnumber ? handleSalesNumberClick(card.salesnumber) : null}
                            sx={{ 
                              cursor: card.salesnumber ? 'pointer' : 'default',
                              color: card.salesnumber ? 'primary.main' : 'inherit',
                              '&:hover': {
                                textDecoration: card.salesnumber ? 'underline' : 'none',
                              },
                              fontWeight: 'medium'
                            }}
                          >
                            {card.salesnumber || '-'}
                          </Box>
                        </Tooltip>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {formatDate(card.submitdate)}
                        </Typography>
                      </TableCell>
                      
                      {/* Brand/Outlet column */}
                      <TableCell>
                        <Box display="flex" flexDirection="column" gap={0.5}>
                          <Typography variant="body2" fontWeight="medium">
                            {card.brandname || '-'}
                          </Typography>
                          <Typography variant="caption" display="flex" alignItems="center" gap={0.5}>
                            <StoreIcon fontSize="inherit" />
                            {card.outletname || '-'}
                          </Typography>
                        </Box>
                      </TableCell>
                      
                      {/* Feedback column */}
                      <TableCell>
                        <Tooltip title={card.question || ''}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                            {card.question?.length > 40 ? `${card.question.substring(0, 40)}...` : card.question || '-'}
                          </Typography>
                        </Tooltip>
                        
                        <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                          {card.pooranswer ? (
                            <Chip
                              size="small"
                              icon={<ThumbDownIcon fontSize="small" />}
                              label="Poor Feedback"
                              color="error"
                              variant="outlined"
                            />
                          ) : (
                            <Chip
                              size="small"
                              icon={<ThumbUpIcon fontSize="small" />}
                              label="Good Feedback"
                              color="success"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </TableCell>
                      
                      {/* Status column */}
                      <TableCell>
                        {getStatusChip(card.status)}
                      </TableCell>
                      
                      {/* Actions column */}
                      <TableCell align="center">
                        <Tooltip title="View Details">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => card.salesnumber ? handleSalesNumberClick(card.salesnumber) : null}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalRecords}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Modal for displaying questionnaires */}
      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        aria-labelledby="questionnaire-modal-title"
        aria-describedby="questionnaire-modal-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: 1000,
          maxHeight: '90vh',
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 2,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Modal Header */}
          <Box sx={{ 
            p: 3, 
            borderBottom: 1, 
            borderColor: 'divider',
            backgroundColor: 'primary.main',
            color: 'primary.contrastText'
          }}>
            <Typography id="questionnaire-modal-title" variant="h6" component="h2">
              Guest Feedback Details
            </Typography>
            <Typography variant="subtitle2" color="primary.contrastText">
              Sales Number: {selectedSalesNumber}
            </Typography>
          </Box>
          
          {loadingQuestionnaires ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : !gccDetail ? (
            <Box sx={{ p: 3 }}>
              <Alert severity="error">No data found for this sales number.</Alert>
            </Box>
          ) : (
            <>
              {/* Tabs for different sections */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs 
                  value={activeTab} 
                  onChange={(_, newValue) => setActiveTab(newValue)}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab label="Customer Information" />
                  <Tab label="Feedback Responses" />
                  <Tab label="Outlet Details" />
                </Tabs>
              </Box>
              
              {/* Tab Content: Customer Information */}
              {activeTab === 0 && (
                <Box sx={{ p: 3, overflow: 'auto' }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box display="flex" alignItems="center" gap={2} mb={2}>
                            <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                              {gccDetail.customer_name?.charAt(0) || 'G'}
                            </Avatar>
                            <Box>
                              <Typography variant="h6">{gccDetail.customer_name || 'Anonymous'}</Typography>
                              {gccDetail.customer_phone && (
                                <Typography variant="body2" display="flex" alignItems="center" gap={0.5}>
                                  <PhoneIcon fontSize="small" />
                                  {gccDetail.customer_phone}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                          
                          <Divider sx={{ my: 2 }} />
                          
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">Sales Number</Typography>
                              <Typography variant="body1">{gccDetail.sales_number || '-'}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">Submit Date</Typography>
                              <Typography variant="body1">{formatDate(gccDetail.submit_date)}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">Status</Typography>
                              <Box mt={0.5}>{getStatusChip(gccDetail.status)}</Box>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="h6" gutterBottom>Brand & Outlet</Typography>
                          
                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <StoreIcon color="primary" />
                            <Typography variant="body1">{gccDetail.brand_name || '-'}</Typography>
                          </Box>
                          
                          <Typography variant="body2" color="text.secondary">Outlet</Typography>
                          <Typography variant="body1" gutterBottom>{gccDetail.outlet_name || '-'}</Typography>
                          
                          {gccDetail.outlet_address && (
                            <>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Address</Typography>
                              <Typography variant="body1">{gccDetail.outlet_address}</Typography>
                            </>
                          )}
                          
                          {gccDetail.outlet_phone && (
                            <>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Phone</Typography>
                              <Typography variant="body1">{gccDetail.outlet_phone}</Typography>
                            </>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              )}
              
              {/* Tab Content: Feedback Responses */}
              {activeTab === 1 && (
                <Box sx={{ p: 3, overflow: 'auto' }}>
                  {questionnaires.length === 0 ? (
                    <Alert severity="info">No feedback responses found.</Alert>
                  ) : (
                    <Grid container spacing={2}>
                      {questionnaires.map((item, index) => (
                        <Grid item xs={12} key={index}>
                          <Card variant="outlined" sx={{ 
                            mb: 2,
                            borderColor: item.pooranswer ? 'error.light' : 'success.light',
                            borderWidth: item.pooranswer ? 2 : 1
                          }}>
                            <CardContent>
                              <Typography variant="subtitle1" gutterBottom>
                                Question {index + 1}: {item.question}
                              </Typography>
                              
                              <Box sx={{ 
                                p: 2, 
                                bgcolor: 'action.hover', 
                                borderRadius: 1,
                                mt: 1
                              }}>
                                <Typography variant="body2" color="text.secondary">Answer:</Typography>
                                <Typography variant="body1">{item.answer || '-'}</Typography>
                              </Box>
                              
                              {item.pooranswer && (
                                <Box sx={{ 
                                  p: 2, 
                                  bgcolor: 'error.lighter', 
                                  borderRadius: 1,
                                  mt: 2,
                                  border: 1,
                                  borderColor: 'error.light'
                                }}>
                                  <Typography variant="body2" color="error">Poor Feedback:</Typography>
                                  <Typography variant="body1">{item.pooranswer}</Typography>
                                </Box>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Box>
              )}
              
              {/* Tab Content: Outlet Details */}
              {activeTab === 2 && (
                <Box sx={{ p: 3, overflow: 'auto' }}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Outlet Information</Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">Name</Typography>
                          <Typography variant="body1" gutterBottom>{gccDetail.outlet_name || '-'}</Typography>
                          
                          {gccDetail.outlet_address && (
                            <>
                              <Typography variant="body2" color="text.secondary">Address</Typography>
                              <Typography variant="body1" gutterBottom>{gccDetail.outlet_address}</Typography>
                            </>
                          )}
                          
                          {gccDetail.outlet_phone && (
                            <>
                              <Typography variant="body2" color="text.secondary">Phone</Typography>
                              <Typography variant="body1">{gccDetail.outlet_phone}</Typography>
                            </>
                          )}
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">Brand</Typography>
                          <Typography variant="body1">{gccDetail.brand_name || '-'}</Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Box>
              )}
            </>
          )}
          
          {/* Modal Footer */}
          <Box sx={{ 
            p: 2, 
            borderTop: 1, 
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'flex-end'
          }}>
            <Button variant="contained" onClick={handleCloseModal}>
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default GuestCommentCardPage;